import { useEffect, useRef, useState, type DragEvent } from 'react';
import classNames from 'classnames';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, styled } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TextSpan } from '../../Atoms/Text';
import { Icon } from '../../Atoms/Icon';
import { addError } from '../../../store/Error';
import {
  AttachmentUploadConfig,
  FALLBACK_UPLOAD_CONFIG,
  fetchAttachmentUploadConfig,
  partitionAttachmentFiles
} from '../../../store/Attachment';
import { formatFileSize } from '../../../utils/helpers';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});

export interface AttachFilesModalProps {
  open: boolean;
  onClose: () => void;
  onAttach: (files: File[]) => void;
}

export function AttachFilesModal({ open, onClose, onAttach }: AttachFilesModalProps) {
  const [queuedFiles, setQueuedFiles] = useState<File[]>([]);
  const [uploadConfig, setUploadConfig] = useState<AttachmentUploadConfig>(FALLBACK_UPLOAD_CONFIG);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragCounterRef = useRef(0);
  const sessionRef = useRef(0);

  useEffect(() => {
    if (open) {
      sessionRef.current += 1;
      setQueuedFiles([]);
      setIsDraggingOver(false);
      dragCounterRef.current = 0;
      void fetchAttachmentUploadConfig().then(setUploadConfig);
    }
  }, [open]);

  const handleFiles = async (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    const session = sessionRef.current;
    const config = await fetchAttachmentUploadConfig();
    if (sessionRef.current !== session) return;
    const { validFiles, rejections } = partitionAttachmentFiles(files, config);
    if (rejections.length) addError('attachment', rejections.join('; '));
    if (validFiles.length) setQueuedFiles((prev) => [...prev, ...validFiles]);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragCounterRef.current = 0;
    setIsDraggingOver(false);
    if (event.dataTransfer.files?.length) void handleFiles(event.dataTransfer.files);
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragCounterRef.current += 1;
    setIsDraggingOver(true);
  };

  const handleDragLeave = () => {
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDraggingOver(false);
    }
  };

  const handleRemoveQueued = (index: number) => {
    setQueuedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    setQueuedFiles([]);
    onClose();
  };

  const handleAttach = () => {
    onAttach(queuedFiles);
    setQueuedFiles([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth="xs"
      data-testid="attach-files-modal">
      <DialogTitle className="flex items-center justify-between">
        <TextSpan
          color="grey9"
          size="lg">
          Attach Files
        </TextSpan>
        <IconButton
          onClick={handleCancel}
          size="small"
          data-testid="attach-files-close-button">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div
          className={classNames(
            'border-dashed bg-grey-1 rounded flex flex-col items-center justify-center gap-1.5 py-7 px-6',
            isDraggingOver ? 'border-2 border-indigo' : 'border border-grey-3 my-[1px]'
          )}
          onDragEnter={handleDragEnter}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          data-testid="attach-files-drop-zone">
          <TextSpan
            color="grey8"
            size="xs">
            Drag files here or{' '}
            <label
              className="text-indigo cursor-pointer"
              htmlFor="attach-files-input">
              browse
            </label>
          </TextSpan>
          <TextSpan
            color="grey4"
            size="xs"
            className="text-[10px]">
            Up to {formatFileSize(uploadConfig.maxSizeBytes)} per file · PDF, Office, images, email
          </TextSpan>
          <VisuallyHiddenInput
            id="attach-files-input"
            type="file"
            multiple
            accept={uploadConfig.allowedExtensions.map((extension) => `.${extension}`).join(',')}
            onChange={(event) => {
              if (event.target.files?.length) void handleFiles(event.target.files);
              event.target.value = '';
            }}
          />
        </div>
        {queuedFiles.length > 0 && (
          <div className="mt-2">
            {queuedFiles.map((file, index) => (
              <div
                className="flex items-center gap-3 py-2"
                key={`${file.name}-${file.size}-${index}`}>
                <TextSpan
                  color="grey8"
                  size="xs"
                  className="shrink-0">
                  {file.name}
                </TextSpan>
                <div className="grow" />
                <TextSpan
                  color="grey4"
                  size="xs"
                  className="shrink-0">
                  {formatFileSize(file.size)}
                </TextSpan>
                <button
                  type="button"
                  onClick={() => handleRemoveQueued(index)}
                  aria-label={`Remove ${file.name}`}
                  data-testid="attach-files-remove-queued-button">
                  <Icon
                    type="cancel"
                    className="w-3 h-3"
                  />
                </button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          className="bg-indigo-light text-grey-8"
          data-testid="attach-files-cancel-button">
          Cancel
        </Button>
        <Button
          onClick={handleAttach}
          disabled={queuedFiles.length === 0}
          className="bg-indigo text-white disabled:opacity-40"
          data-testid="attach-files-attach-button">
          Attach
        </Button>
      </DialogActions>
    </Dialog>
  );
}
